import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import AguaShell from "../components/AguaShell";
import {
  eliminarLectura, exportarExcel, getFilialesAgua, getLecturas,
} from "../services/aguaService";
import { AZUL, VERDE, aguaTheme, fechaCorta, m3 } from "../theme";

export default function AguaHistorial() {
  const { t }     = useTranslation();
  const { theme } = useTheme();
  const isLight   = theme === "light";
  const tema      = aguaTheme(isLight);
  const { showToast, toastEl } = useToast();

  const [filiales, setFiliales] = useState([]);
  const [lecturas, setLecturas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error,    setError]    = useState("");

  // El panel de resumen enlaza acá con ?filial=N (alertas de fuga), así que la
  // pantalla arranca ya filtrada por esa filial.
  const [searchParams] = useSearchParams();
  const [filtros, setFiltros] = useState({
    filial: searchParams.get("filial") || "",
    fecha: "", desde: "", hasta: "",
  });
  const [modoFecha, setModoFecha] = useState("rango");  // 'rango' | 'exacta'
  const [zoom,     setZoom]     = useState(null);       // url de la foto ampliada
  const [aBorrar,  setABorrar]  = useState(null);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => { getFilialesAgua().then(setFiliales).catch(() => {}); }, []);

  // Los filtros de fecha que no correspondan al modo activo se mandan vacíos,
  // si no el backend aplicaría los dos a la vez y no devolvería nada.
  const params = useMemo(() => ({
    filial: filtros.filial,
    fecha:  modoFecha === "exacta" ? filtros.fecha : "",
    desde:  modoFecha === "rango"  ? filtros.desde : "",
    hasta:  modoFecha === "rango"  ? filtros.hasta : "",
  }), [filtros, modoFecha]);

  const cargar = useCallback(() => {
    setCargando(true); setError("");
    getLecturas(params)
      .then(d => setLecturas(d.results || d))
      .catch(e => setError(e.message))
      .finally(() => setCargando(false));
  }, [params]);

  useEffect(() => { cargar(); }, [cargar]);

  async function confirmarBorrado() {
    if (!aBorrar) return;
    setBorrando(true);
    try {
      await eliminarLectura(aBorrar.id);
      showToast(t("agua.lecturaEliminada"), true);
      setABorrar(null);
      cargar();
    } catch (e) {
      showToast(e.message, false);
    } finally { setBorrando(false); }
  }

  async function descargar() {
    try {
      await exportarExcel(params);
    } catch (e) { showToast(e.message, false); }
  }

  const totalConsumo = lecturas.reduce(
    (acc, l) => acc + (l.consumo != null ? parseFloat(l.consumo) : 0), 0
  );

  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 0.7,
    textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 7,
  };

  return (
    <AguaShell
      titulo={t("agua.tituloHistorial")}
      descripcion={t("agua.descHistorial")}
      icono="fa-clock-rotate-left"
      acciones={
        <button onClick={descargar} className="agua-btn" style={{
          background: VERDE, color: "#fff", padding: "8px 14px", fontSize: 13,
        }}>
          <i className="fa-solid fa-file-excel" /> {t("agua.exportarExcel")}
        </button>
      }
    >
      {toastEl}

      {/* ── Filtros ── */}
      <div className="agua-card" style={{ marginBottom: 14, boxShadow: tema.cardShadow }}>
        <div className="agua-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))" }}>
          <div>
            <label style={labelStyle}>{t("agua.filial")}</label>
            <select className="form-control" value={filtros.filial}
                    onChange={e => setFiltros(f => ({ ...f, filial: e.target.value }))}
                    style={{ width: "100%" }}>
              <option value="">{t("agua.todasLasFiliales")}</option>
              {filiales.map(f => (
                <option key={f.id} value={f.id}>{f.idFilial}</option>
              ))}
            </select>
          </div>

          {modoFecha === "exacta" ? (
            <div>
              <label style={labelStyle}>{t("agua.fechaExacta")}</label>
              <input type="date" className="form-control" value={filtros.fecha}
                     onChange={e => setFiltros(f => ({ ...f, fecha: e.target.value }))}
                     style={{ width: "100%", boxSizing: "border-box" }} />
            </div>
          ) : (
            <>
              <div>
                <label style={labelStyle}>{t("agua.desde")}</label>
                <input type="date" className="form-control" value={filtros.desde}
                       onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))}
                       style={{ width: "100%", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={labelStyle}>{t("agua.hasta")}</label>
                <input type="date" className="form-control" value={filtros.hasta}
                       onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))}
                       style={{ width: "100%", boxSizing: "border-box" }} />
              </div>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button onClick={() => setModoFecha(m => m === "rango" ? "exacta" : "rango")}
                  className="agua-btn" style={{
            background: "transparent", color: AZUL, border: `1px solid ${AZUL}`,
            padding: "8px 13px", fontSize: 12.5,
          }}>
            <i className="fa-solid fa-calendar-day" />
            {modoFecha === "rango" ? t("agua.usarFechaExacta") : t("agua.usarRango")}
          </button>
          <button onClick={() => setFiltros({ filial: "", fecha: "", desde: "", hasta: "" })}
                  className="agua-btn" style={{
            background: "transparent", color: "var(--text-secondary)",
            border: "1px solid var(--border-input)", padding: "8px 13px", fontSize: 12.5,
          }}>
            <i className="fa-solid fa-eraser" /> {t("agua.limpiarFiltros")}
          </button>
        </div>
      </div>

      {/* ── Totales del filtro ── */}
      {!cargando && lecturas.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            { icono: "fa-list-ol", label: t("agua.lecturasEncontradas"), valor: lecturas.length },
            { icono: "fa-droplet", label: t("agua.consumoAcumulado"),    valor: m3(totalConsumo) },
          ].map(({ icono, label, valor }) => (
            <div key={label} className="agua-card" style={{
              flex: "1 1 160px", padding: "14px 16px", boxShadow: tema.cardShadow,
              display: "flex", alignItems: "center", gap: 11,
            }}>
              <i className={`fa-solid ${icono}`} style={{ color: AZUL, fontSize: 16 }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
                              textTransform: "uppercase", color: "var(--text-secondary)" }}>
                  {label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text-primary)" }}>
                  {valor}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="agua-card" style={{
          marginBottom: 14, color: "#ef4444",
          background: "rgba(239,68,68,0.09)", border: "1px solid rgba(239,68,68,0.3)",
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 8 }} />{error}
        </div>
      )}

      {/* ── Listado ── */}
      {cargando ? (
        <p style={{ textAlign: "center", color: "var(--text-secondary)", padding: 32 }}>
          {t("common.cargando")}
        </p>
      ) : lecturas.length === 0 ? (
        <div className="agua-card" style={{ textAlign: "center", padding: 56, color: "var(--text-secondary)" }}>
          <i className="fa-solid fa-droplet-slash" style={{ fontSize: 38, opacity: .3, display: "block", marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>{t("agua.sinResultados")}</p>
        </div>
      ) : (
        <div className="agua-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))" }}>
          {lecturas.map(l => (
            <div key={l.id} className="agua-card" style={{
              boxShadow: tema.cardShadow, borderLeft: `4px solid ${AZUL}`,
              display: "flex", flexDirection: "column", gap: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text-primary)" }}>
                    {l.filial_nombre}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 }}>
                    <i className="fa-solid fa-calendar" style={{ marginRight: 6 }} />
                    {fechaCorta(l.fecha)}
                  </div>
                </div>
                <button onClick={() => setABorrar(l)} title={t("common.eliminar")} style={{
                  border: "none", background: "transparent", cursor: "pointer",
                  color: "var(--text-secondary)", padding: 6, borderRadius: 8, fontSize: 14,
                }}>
                  <i className="fa-solid fa-trash-can" />
                </button>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{
                  flex: "1 1 120px", padding: "10px 12px", borderRadius: 10,
                  background: tema.chipBg, border: `1px solid ${tema.chipBorder}`,
                }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
                                textTransform: "uppercase", color: tema.chipText }}>
                    {t("agua.lectura")}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text-primary)" }}>
                    {m3(l.lectura)}
                  </div>
                </div>
                <div style={{
                  flex: "1 1 120px", padding: "10px 12px", borderRadius: 10,
                  background: l.consumo != null ? "rgba(16,185,129,0.09)" : "var(--hover-bg)",
                  border: `1px solid ${l.consumo != null ? "rgba(16,185,129,0.28)" : "var(--border-card)"}`,
                }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
                                textTransform: "uppercase",
                                color: l.consumo != null ? VERDE : "var(--text-secondary)" }}>
                    {t("agua.consumo")}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text-primary)" }}>
                    {l.consumo != null ? m3(l.consumo) : t("agua.primeraLectura")}
                  </div>
                </div>
              </div>

              {l.foto_url ? (
                <button onClick={() => setZoom(l.foto_url)} style={{
                  border: "none", padding: 0, background: "transparent",
                  cursor: "zoom-in", borderRadius: 11, overflow: "hidden", display: "block",
                }}>
                  <img src={l.foto_url} alt={`${l.filial_nombre} ${l.fecha}`} loading="lazy"
                       style={{ width: "100%", height: 170, objectFit: "cover", display: "block" }} />
                </button>
              ) : (
                <div style={{
                  padding: "14px", borderRadius: 11, textAlign: "center",
                  border: "1px dashed var(--border-input)",
                  color: "var(--text-secondary)", fontSize: 12.5,
                }}>
                  <i className="fa-solid fa-image" style={{ marginRight: 7, opacity: .6 }} />
                  {t("agua.sinFoto")}
                </div>
              )}

              {l.notas && (
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5 }}>
                  {l.notas}
                </p>
              )}

              {l.registrado_por_nombre && (
                <div style={{ fontSize: 11.5, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-user" style={{ marginRight: 6, opacity: .7 }} />
                  {l.registrado_por_nombre}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Foto ampliada ── */}
      {zoom && (
        <div onClick={() => setZoom(null)} style={{
          position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20, cursor: "zoom-out",
        }}>
          <img src={zoom} alt="" style={{
            maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 10,
          }} />
          <button onClick={() => setZoom(null)} style={{
            position: "absolute", top: 18, right: 18, border: "none", cursor: "pointer",
            width: 42, height: 42, borderRadius: "50%", fontSize: 17,
            background: "rgba(255,255,255,0.15)", color: "#fff",
          }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      <ConfirmModal
        open={!!aBorrar}
        name={aBorrar ? `${aBorrar.filial_nombre} · ${fechaCorta(aBorrar.fecha)}` : ""}
        message={t("agua.confirmarEliminar")}
        loading={borrando}
        onConfirm={confirmarBorrado}
        onCancel={() => setABorrar(null)}
      />
    </AguaShell>
  );
}
